import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail, MapPin, Github, Linkedin, Loader2, Check } from "lucide-react";
import { Section, SectionHeading } from "./_shared";
import { publicApi, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const Contact = ({ contact = {}, hero = {} }) => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const socials = contact.socials || hero.socials || {};
  const ownerEmail = contact.email || hero.email;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.contact(form);
      setSent(true);
      toast.success("Message sent — I'll get back to you soon.");
      setForm({ name: "", email: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 4500);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Section id="contact">
      <SectionHeading index="07" label="Contact" title="Let's build something." description="Open to AI / ML roles, research collabs and ambitious freelance briefs." testId="contact-heading" />
      <div className="grid lg:grid-cols-12 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 glass rounded-3xl p-7 md:p-8"
          data-testid="contact-info"
        >
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-cyan-300 mb-4">Direct</div>
          <h3 className="font-display text-2xl text-white font-medium">{hero.name}</h3>
          <p className="mt-2 text-slate-400 text-sm leading-relaxed">Prefer email or LinkedIn DMs. I typically respond within 24 hours.</p>
          <div className="mt-6 space-y-3">
            {ownerEmail && (
              <a href={`mailto:${ownerEmail}`} data-testid="contact-email-link" className="flex items-center gap-3 text-slate-300 hover:text-white transition-colors">
                <span className="w-9 h-9 rounded-lg border border-white/10 grid place-items-center bg-white/[0.03]"><Mail size={14} /></span>
                <span className="text-sm">{ownerEmail}</span>
              </a>
            )}
            {(contact.location || hero.location) && (
              <div className="flex items-center gap-3 text-slate-400">
                <span className="w-9 h-9 rounded-lg border border-white/10 grid place-items-center bg-white/[0.03]"><MapPin size={14} /></span>
                <span className="text-sm">{contact.location || hero.location}</span>
              </div>
            )}
          </div>
          <div className="mt-6 pt-6 border-t border-white/5 flex gap-2">
            {socials.github && <a href={socials.github} target="_blank" rel="noreferrer" data-testid="contact-github" className="w-10 h-10 rounded-lg glass grid place-items-center text-slate-300 hover:text-white"><Github size={16} /></a>}
            {socials.linkedin && <a href={socials.linkedin} target="_blank" rel="noreferrer" data-testid="contact-linkedin" className="w-10 h-10 rounded-lg glass grid place-items-center text-slate-300 hover:text-white"><Linkedin size={16} /></a>}
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.08 }}
          onSubmit={onSubmit}
          noValidate
          className="lg:col-span-7 glass rounded-3xl p-7 md:p-8 space-y-5"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">Your Name</span>
              <input type="text" value={form.name} onChange={update("name")} placeholder="Ada Lovelace" data-testid="contact-input-name" className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50" />
            </label>
            <label className="block">
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">Email</span>
              <input type="email" value={form.email} onChange={update("email")} placeholder="ada@example.com" data-testid="contact-input-email" className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50" />
            </label>
          </div>
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">Subject</span>
            <input type="text" value={form.subject} onChange={update("subject")} placeholder="Let's collaborate on..." data-testid="contact-input-subject" className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50" />
          </label>
          <label className="block">
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">Message</span>
            <textarea value={form.message} onChange={update("message")} placeholder="Tell me about your project, role, or idea..." rows={6} data-testid="contact-input-message" className="mt-2 w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 focus:outline-none focus:border-cyan-400/50 resize-none" />
          </label>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <span className="text-xs text-slate-500">I respect your inbox — no spam, ever.</span>
            <button type="submit" disabled={submitting || sent} data-testid="contact-submit" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-black font-medium text-sm disabled:opacity-70">
              {submitting ? <><Loader2 size={15} className="animate-spin" /> Sending...</> : sent ? <><Check size={15} /> Sent</> : <>Send Message <Send size={14} /></>}
            </button>
          </div>
        </motion.form>
      </div>
    </Section>
  );
};

export default Contact;
